import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUserContext } from 'App/contexts/UserContext';
import useChangeBranch from 'App/hooks/useChangeBranch';
import { workQueueRoute } from 'App/common/routeLookup';
import { patientPropType } from 'Shared/types';
import toast from 'Shared/toast';

const UserBranchSwitcher = ({ patient }) => {
    const user = useUserContext();
    const { handleChangeBranch, isChanging } = useChangeBranch();
    const navigate = useNavigate();

    // Track the last divisionId we attempted to switch to
    const lastRequestedDivisionRef = useRef(null);
    // Mirror isChanging in a ref so we can read it in the effect without adding it to deps
    const isChangingRef = useRef(isChanging);
    useEffect(() => {
        isChangingRef.current = isChanging;
    }, [isChanging]);

    // Reset the guard whenever the target patient changes
    useEffect(() => {
        lastRequestedDivisionRef.current = null;
    }, [patient?.divisionId]);

    useEffect(() => {
        // Guard against user not being loaded yet
        if (!user || !user.active_branch) {
            return;
        }

        const targetDivisionId = patient?.divisionId;
        const currentBranchId = user.active_branch.ID;

        // If there is no target division, do nothing
        if (!targetDivisionId) {
            return;
        }

        const alreadyRequestedThisTarget = lastRequestedDivisionRef.current === targetDivisionId;
        const needsBranchChange = targetDivisionId !== currentBranchId;

        if (!isChangingRef.current && needsBranchChange && !alreadyRequestedThisTarget) {
            lastRequestedDivisionRef.current = targetDivisionId;

            handleChangeBranch(targetDivisionId).catch(() => {
                // Do not clear lastRequestedDivisionRef — prevents infinite retry loop.
                // Redirect the user to the work queue since we cannot safely display
                // a patient that belongs to a branch the session failed to switch to.
                toast.error('Unable to switch to the required branch. Redirecting to the Work Queue.');
                navigate(workQueueRoute);
            });
        }
    }, [handleChangeBranch, patient?.divisionId, user, navigate]);

    return null;
};

UserBranchSwitcher.propTypes = {
    patient: patientPropType.isRequired,
};

export default UserBranchSwitcher;
